/**
 * AccentSparkle - Lucide Sparkles icon with Asklaw ember gradient fill via SVG linearGradient.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 2. Per spec F-FE-THEME-GEMINI §7.6 + AC-09 (gradient
 * chi xuat hien o 3 noi: greeting heading + sparkle + landing hero radial).
 *
 * KHONG sao chep sparkle cua Google Gemini brand mark - SVG path la Lucide MIT-licensed,
 * mau fill la token Asklaw `--asklaw-gradient` (orange ember).
 *
 * Animation: subtle pulse scale 1 -> 1.05 -> 1 (1200ms ease-in-out infinite). Respect
 * `prefers-reduced-motion: reduce` -> static (no pulse).
 *
 * Usage (max 2 instance per page per AC-09):
 *   <AccentSparkle size={32} />  // greeting hero
 *   <AccentSparkle size={24} />  // suggestion empty / FAB
 */

import React from 'react';

export type AccentSparkleProps = {
  size?: number;
  className?: string;
  'aria-label'?: string;
};

let uidCounter = 0;
function nextUid(): string {
  uidCounter += 1;
  return `accent-sparkle-${uidCounter}`;
}

export function AccentSparkle({
  size = 24,
  className,
  'aria-label': ariaLabel,
}: AccentSparkleProps) {
  // Stable gradient id per instance so multiple sparkles on same page do not collide
  const gradId = React.useMemo(() => nextUid(), []);
  const hasLabel = Boolean(ariaLabel);

  return (
    <span
      className={['accent-sparkle', className].filter(Boolean).join(' ')}
      role={hasLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      aria-hidden={hasLabel ? undefined : true}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="var(--asklaw-gradient-stop-1, #F38020)" />
            <stop offset="50%" stopColor="var(--asklaw-gradient-stop-2, #FAAD3F)" />
            <stop offset="100%" stopColor="var(--asklaw-gradient-stop-3, #FF6A1B)" />
          </linearGradient>
        </defs>
        {/* Lucide Sparkles path - MIT */}
        <path
          d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z"
          fill={`url(#${gradId})`}
        />
        <path
          d="M19 14l.9 2.2L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.8L19 14z"
          fill={`url(#${gradId})`}
        />
        <path
          d="M5 16l.7 1.6L7.3 18l-1.6.7L5 20l-.7-1.3L3 18l1.6-.7L5 16z"
          fill={`url(#${gradId})`}
        />
      </svg>
    </span>
  );
}

export default AccentSparkle;
